import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MultiSelect } from '../ui/multi-select';
import { Upload, X, User as UserIcon } from 'lucide-react';
import { Member, SecurityRole } from '../../types';
import { useOrganizationalRoles } from '../../hooks';
import { OffboardingFields } from '../people/OffboardingFields';
import { dateToInputValue } from '../../utils/formatters';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';

interface PersonInfoCardProps {
  person: Member;
  allPeople: Member[];
  allClientTags: string[];
  onSave: (data: PersonFormData, photo: File | null) => Promise<void>;
  saving: boolean;
  /** Only admins may change the access role; everyone else sees it read-only. */
  canEditSecurityRole?: boolean;
}

export interface PersonFormData {
  name: string;
  email: string;
  reportsTo: string;
  organizationRole: string;
  /** Authorization role held in the organization; empty when the member has no account. */
  securityRole: string;
  clients: string[];
  birthDate: string;
  hireDate: string;
  isActive: boolean;
  contractRate: string;
  offboardingDate: string;
  offboardingReason: string;
}

// Roles an admin may assign. OWNER changes through ownership transfer and SUPPORT is managed by
// platform support, so they are only ever displayed.
const ASSIGNABLE_SECURITY_ROLES: SecurityRole[] = [
  SecurityRole.ADMIN,
  SecurityRole.BILLING,
  SecurityRole.MANAGER,
  SecurityRole.LEAD,
  SecurityRole.MEMBER,
  SecurityRole.VIEWER,
];

export const PersonInfoCard: React.FC<PersonInfoCardProps> = ({
  person,
  allPeople,
  allClientTags,
  onSave,
  saving,
  canEditSecurityRole = false,
}) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation('common');
  const { data: organizationalRoles = [] } = useOrganizationalRoles(false);
  
  // Sort roles by display order, then by name
  const sortedRoles = [...organizationalRoles].sort((a, b) => {
    if (a.displayOrder !== b.displayOrder) {
      return a.displayOrder - b.displayOrder;
    }
    return a.name.localeCompare(b.name);
  });
  
  const [formData, setFormData] = useState<PersonFormData>({
    name: person.name,
    email: person.email,
    reportsTo: person.reportsTo?.id || '__none__',
    organizationRole: person.organizationRole || 'Engineer',
    securityRole: person.securityRole || '',
    clients: person.clients || [],
    birthDate: dateToInputValue(person.birthDate),
    hireDate: dateToInputValue(person.hireDate),
    isActive: person.isActive !== false,
    contractRate: person.contractRate?.toString() || '',
    offboardingDate: dateToInputValue(person.offboardingDate),
    offboardingReason: person.offboardingReason || '',
  });

  const currentSecurityRole = person.securityRole;
  const securityRoleLocked =
    !currentSecurityRole || !ASSIGNABLE_SECURITY_ROLES.includes(currentSecurityRole);
  const securityRoleOptions =
    currentSecurityRole && !ASSIGNABLE_SECURITY_ROLES.includes(currentSecurityRole)
      ? [currentSecurityRole, ...ASSIGNABLE_SECURITY_ROLES]
      : ASSIGNABLE_SECURITY_ROLES;
  const securityRoleHint = !currentSecurityRole
    ? t('form.securityRole.noAccount')
    : securityRoleLocked
    ? t('form.securityRole.locked')
    : !canEditSecurityRole
    ? t('form.securityRole.readOnly')
    : t('form.securityRole.help');

  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(person.profilePictureUrl || person.photoURL || null);
  const [error, setError] = useState<string | null>(null);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert(t('form.photoSizeError'));
        return;
      }
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    setPhotoPreview(person.profilePictureUrl || person.photoURL || null);
  };

  const handleSave = async () => {
    setError(null);
    
    // Client-side validation - stricter email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email || !emailRegex.test(formData.email.trim())) {
      setError('Please enter a valid email address (e.g., user@example.com)');
      return;
    }
    
    try {
      await onSave(formData, photo);
    } catch (err: any) {
      console.error('Save error:', err);
      // Handle NestJS validation errors (array of messages)
      if (err.response?.data?.message && Array.isArray(err.response.data.message)) {
        setError(err.response.data.message.join(', '));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.status === 400) {
        setError('Invalid data. Please check all fields and try again.');
      } else {
        setError(err.message || 'Failed to save person');
      }
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('profile.title')}</CardTitle>
        <Button
          onClick={handleSave}
          size="sm"
          disabled={saving || !formData.name || !formData.email}
        >
          {saving ? tCommon('saving') : t('profile.saveChanges')}
        </Button>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-6">
          {/* Photo Upload */}
          <div className="flex items-center gap-6">
            <div className="relative">
              <Avatar className="h-24 w-24">
                {photoPreview ? (
                  <AvatarImage src={photoPreview} alt={formData.name} />
                ) : null}
                <AvatarFallback className="bg-primary/10 text-primary text-2xl">
                  {formData.name?.charAt(0) || <UserIcon className="h-12 w-12" />}
                </AvatarFallback>
              </Avatar>
              {photo && (
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            <div>
              <Label htmlFor="photo" className="cursor-pointer">
                <div className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-muted transition-colors">
                  <Upload className="h-4 w-4" />
                  <span className="text-sm">{t('form.uploadPhoto')}</span>
                </div>
              </Label>
              <input
                id="photo"
                type="file"
                className="hidden"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handlePhotoChange}
              />
              <p className="text-xs text-muted-foreground mt-1">{t('form.maxFileSize')}</p>
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('form.labels.name')} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('form.labels.email')} *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="org-role">{t('form.labels.organizationRole')} *</Label>
              <Select
                value={formData.organizationRole}
                onValueChange={(value) =>
                  setFormData(prev => ({ ...prev, organizationRole: value }))
                }
              >
                <SelectTrigger id="org-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortedRoles.map((role) => (
                    <SelectItem key={role.id} value={role.name}>
                      <div className="flex items-center gap-2">
                        {role.name}
                        {role.isAdmin && (
                          <Badge variant="default" className="bg-purple-600 text-xs">
                            Admin
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="security-role">{t('form.labels.securityRole')}</Label>
              <Select
                value={formData.securityRole}
                onValueChange={(value) => setFormData(prev => ({ ...prev, securityRole: value }))}
                disabled={!canEditSecurityRole || securityRoleLocked}
              >
                <SelectTrigger id="security-role" data-testid="person-info-select-security-role">
                  <SelectValue placeholder={t('form.securityRole.noAccount')} />
                </SelectTrigger>
                <SelectContent>
                  {securityRoleOptions.map((role) => (
                    <SelectItem key={role} value={role} data-testid={`person-info-security-role-${role}`}>
                      {t(`form.securityRole.options.${role}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground" data-testid="person-info-security-role-hint">
                {securityRoleHint}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reports-to">{t('form.labels.reportsTo')}</Label>
              <Select
                value={formData.reportsTo}
                onValueChange={(value) => setFormData(prev => ({ ...prev, reportsTo: value }))}
              >
                <SelectTrigger id="reports-to">
                  <SelectValue placeholder={t('form.placeholders.selectManager')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{tCommon('none')}</SelectItem>
                  {allPeople
                    .filter((p) => p.id !== person.id)
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="birth-date">{t('form.labels.birthDate')}</Label>
              <Input
                id="birth-date"
                type="date"
                value={formData.birthDate}
                onChange={(e) => setFormData(prev => ({ ...prev, birthDate: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hire-date">{t('form.labels.hireDate')}</Label>
              <Input
                id="hire-date"
                type="date"
                value={formData.hireDate}
                onChange={(e) => setFormData(prev => ({ ...prev, hireDate: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contract-rate">Contract Rate</Label>
              <Input
                id="contract-rate"
                type="number"
                step="0.01"
                min="0"
                value={formData.contractRate}
                onChange={(e) => setFormData(prev => ({ ...prev, contractRate: e.target.value }))}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">{t('form.labels.status')}</Label>
              <Select
                value={formData.isActive ? 'active' : 'inactive'}
                onValueChange={(value) =>
                  setFormData(prev => ({ ...prev, isActive: value === 'active' }))
                }
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{tCommon('status.active')}</SelectItem>
                  <SelectItem value="inactive">{tCommon('status.inactive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!formData.isActive && (
              <OffboardingFields
                offboardingDate={formData.offboardingDate}
                offboardingReason={formData.offboardingReason}
                onDateChange={(value) =>
                  setFormData(prev => ({ ...prev, offboardingDate: value }))
                }
                onReasonChange={(value) =>
                  setFormData(prev => ({ ...prev, offboardingReason: value }))
                }
                idPrefix="person-info-offboarding"
                showDateHelper
              />
            )}

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="clients">{t('form.labels.clients')}</Label>
              <MultiSelect
                value={formData.clients}
                onChange={(clients) => setFormData(prev => ({ ...prev, clients }))}
                options={allClientTags}
                placeholder={t('form.placeholders.clients')}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
